/**
 * usePermission Hook
 *
 * Custom hook for checking permissions in functional components
 * Provides easy-to-use permission checking functions
 */

import { usePermissionContext } from '../contexts/PermissionContext';

/**
 * Hook for permission checking
 *
 * @returns {Object} Permission checking functions
 *
 * @example
 * const { hasPermission, hasAnyPermission, can } = usePermission();
 *
 * if (hasPermission('ZONE_CREATE')) {
 *   // Show create button
 * }
 *
 * if (hasAnyPermission(['ZONE_UPDATE', 'ZONE_DELETE'])) {
 *   // Show edit/delete buttons
 * }
 *
 * if (can('create', 'ZONE')) {
 *   // Alternative syntax
 * }
 */
export const usePermission = () => {
  const {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasNonePermissions,
    loading,
    loadPermissions,
  } = usePermissionContext();

  // No auto-reload needed - permissions are loaded on mount and after login

  /**
   * Alternative syntax: can('create', 'ZONE') instead of hasPermission('ZONE_CREATE')
   *
   * @param {string} action - The action (create, update, delete, view, etc.)
   * @param {string} entity - The entity (ZONE, COMPANY, PACKAGE, etc.)
   * @returns {boolean}
   */
  const can = (action, entity) => {
    const permission = `${entity.toUpperCase()}_${action.toUpperCase()}`;
    return hasPermission(permission);
  };

  /**
   * Check if user cannot perform an action
   */
  const cannot = (action, entity) => {
    return !can(action, entity);
  };

  /**
   * Check if user can perform any of the actions on an entity
   */
  const canAny = (actions, entity) => {
    const permissionList = actions.map(action =>
      `${entity.toUpperCase()}_${action.toUpperCase()}`
    );
    return hasAnyPermission(permissionList);
  };

  /**
   * Check if user can perform all of the actions on an entity
   */
  const canAll = (actions, entity) => {
    const permissionList = actions.map(action =>
      `${entity.toUpperCase()}_${action.toUpperCase()}`
    );
    return hasAllPermissions(permissionList);
  };

  /**
   * Get all user permissions
   */
  const getPermissions = () => {
    return permissions;
  };

  /**
   * Check if user has any permissions at all
   */
  const hasAnyPermissions = () => {
    return permissions && permissions.length > 0;
  };

  return {
    // Context functions
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasNonePermissions,
    loading,
    loadPermissions,

    // Convenience functions
    can,
    cannot,
    canAny,
    canAll,
    getPermissions,
    hasAnyPermissions,
  };
};

export default usePermission;
